import { apiClient } from '@/lib/apiClient';
import type { QueryOptions } from '@/lib/reactQuery';
import type { DateString, Id, MinutesCount, Uuid } from '@/types/api';
import type { AppointmentStatus, SessionType } from '@/types/scheduling';
import { useQuery } from '@tanstack/react-query';

export interface SessionSummary {
  id: Id;
  numberOfSkillsMasteredAndCompleted: number;
  numberOfSkillsInProgress: number;
  numberOfPagesCompleted: number;
  numberOfStarsEarned: number;
  didWorkOnAndCheckSchoolwork: boolean;
  didCompleteAndCheckSchoolwork: boolean;
}

export interface LastSession {
  studentId: Id;
  appointmentDate: DateString;
  durationMinutes: MinutesCount;
  sessionType: SessionType | null;
  status: AppointmentStatus | null;
  appointmentId: Uuid | null;
  finalized?: boolean | null;
  attendanceId: Id | null;
  sessionSummary?: SessionSummary;
}

export interface GetLastAppointmentResponseData {
  session: LastSession | null;
}

export const getLastAppointment = async ({ ...params }: { studentId: Id }) => {
  const response = await apiClient.get<GetLastAppointmentResponseData>('/v1/scheduling/appointments/last', { params });
  return response.data;
};

interface UseGetLastAppointmentOptions {
  studentId: Id;
  options?: QueryOptions<GetLastAppointmentResponseData>;
}

export const GET_LAST_APPOINTMENT_QUERY_KEY = 'getLastAppointment';

export const useGetLastAppointment = ({ studentId, options }: UseGetLastAppointmentOptions) => {
  return useQuery({
    queryKey: [GET_LAST_APPOINTMENT_QUERY_KEY, studentId],
    queryFn: () => getLastAppointment({ studentId }),
    ...options,
  });
};
